import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { type SelectFilterProps } from '@/components/select-filter/select-filter'

import { caseUpdateHistoryFilterQuery } from '@/lib/queries/notification/v1/history/filters/queries'
import { NotificationType } from '@/lib/queries/notification/v1/history/filters/types'

export const useCaseUpdateHistoryFilters = () => {
  const navigate = useNavigate()
  const searchParams = useSearch({
    from: '/_app/_dashboard/dashboard/_caseUpdates/case-updates/case-update-history',
  })

  const { data: caseUpdateHistoryFilterData } = useQuery(
    caseUpdateHistoryFilterQuery({
      notificationType: NotificationType.CASE_UPDATES,
    })
  )

  const [searchInputText, setSearchInputText] = useState('')

  const assigneeFilterValues = searchParams.assignees ?? []
  const assigneeFilterOption = searchParams.assigneeCondition
  const assigneeFilterItems =
    caseUpdateHistoryFilterData?.assignees.map((item) => ({
      ...item,
      filterValue: `${item.filterValue}`,
    })) ?? []
  const isFilterApplied = assigneeFilterValues.length > 0

  const resetFilter = () => {
    void navigate({
      to: '/dashboard/case-updates/case-update-history',
      search: {
        page: 1,
      },
    })
  }

  const searchWithKeyword = useDebouncedCallback((keyword: string) => {
    void navigate({
      to: '/dashboard/case-updates/case-update-history',
      search: {
        ...searchParams,
        keyword: keyword ? keyword : undefined,
      },
    })
  }, 300)

  const handleChangeSearchInputText = (value: string) => {
    setSearchInputText(value)
    searchWithKeyword(value)
  }

  const handleChangeAssigneeFilterValues: SelectFilterProps['onChangeValue'] = (values) => {
    void navigate({
      to: '/dashboard/case-updates/case-update-history',
      search: {
        ...searchParams,
        assignees: [...values],
      },
    })
  }

  const handleChangeAssigneeFilterOption: SelectFilterProps['onChangeFilterOption'] = (value) => {
    void navigate({
      to: '/dashboard/case-updates/case-update-history',
      search: {
        ...searchParams,
        assigneeCondition: value,
      },
    })
  }

  useEffect(() => {
    if (searchParams.keyword) {
      setSearchInputText(searchParams.keyword)
    }
  }, [searchParams])

  return {
    searchInputText,
    assigneeFilterItems,
    assigneeFilterValues,
    assigneeFilterOption,
    isFilterApplied,
    resetFilter,
    handleChangeSearchInputText,
    handleChangeAssigneeFilterValues,
    handleChangeAssigneeFilterOption,
  }
}
